import {useCampaignListQuery} from "@/api/campaignApi";
import CreateCampaignButton from "@/components/Campaign/CreateCampaignButton";
import Loader from "@/components/Common/kpi/Loader";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {useTrialTabMilestone} from "@/hooks/useTrialTabMilestone";
import {useIsAdmin} from "@/hooks/useUserHooks";
import CampaignCard from "@/pages/campaigns/CampaignCard";
import CampaignFilters from "@/pages/campaigns/CampaignFilters";
import {Can} from "@/permissions";
import useCampaignFilterStore from "@/store/campaignFilter";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import {createFileRoute} from "@tanstack/react-router";
import {useAtomValue} from "jotai";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/campaigns")({
  component: CampaignList,
});

function CampaignList() {
  const {t} = useTranslation("campaign_list");
  const isAdmin = useIsAdmin();
  const searchParams = useCampaignFilterStore(state => state.searchParams);
  useTrialTabMilestone("inspiration_tab");
  const selectedCompanyId = useAtomValue(selectedCompanyIdAtom);

  const campaignsQuery = useCampaignListQuery({searchParams});
  const campaignCount = campaignsQuery.data?.count ?? 0;
  const campaigns = campaignsQuery.data?.results ?? [];
  const {ref} = useInViewWithQuery(campaignsQuery);

  useTitle(t("campaigns_page_title"));

  return (
    <>
      <div className="max-w-wide container mx-auto mb-20 flex flex-col gap-8 p-8">
        {isAdmin && <CampaignFilters />}
        <div className="relative flex items-center justify-between">
          <Can I="create" a="Campaign" passThrough>
            {allowed =>
              !!selectedCompanyId && allowed ? (
                <CreateCampaignButton
                  buttonStyle="BLUE"
                  className="z-10 w-min flex-row-reverse px-9 py-4 whitespace-nowrap"
                />
              ) : (
                <div />
              )
            }
          </Can>
          <span className="text-neutral-black text-2.5xl invisible absolute right-0 left-0 z-0 text-center font-semibold md:visible">
            {t("campaigns")}
          </span>
          <div className="flex items-center justify-center gap-2">
            {campaignsQuery.isPending ? (
              <div className="border-primary-light-solid rounded-md border p-3">
                <Loader />
              </div>
            ) : (
              <span className="border-primary-light-solid text-primary-dark-solid text-2.5xl rounded-md border p-3 font-semibold">
                {campaignCount}
              </span>
            )}
          </div>
        </div>
        {campaignsQuery.isPending ? (
          <Loader
            styles={{
              containerClassName: "min-h-[80vh] w-full items-center",
              loaderClassName: "size-20",
            }}
          />
        ) : (
          <div className="relative mb-20 grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
            {campaigns.map(campaign => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
            {campaignsQuery.isFetching && (
              <Loader
                styles={{
                  containerClassName: "my-auto",
                  loaderClassName: "size-12",
                }}
              />
            )}
            <div ref={ref} />
          </div>
        )}
      </div>
    </>
  );
}
